import { useState } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { procedureApi } from '../services/procedureApi';
import type { ProcedureWithGroupDto, MapProcedureRequest } from '../types/procedure';

interface ProcedureMappingDialogProps {
  procedure: ProcedureWithGroupDto;
  onClose: () => void;
  onSaved: () => void;
}

export default function ProcedureMappingDialog({ procedure, onClose, onSaved }: Readonly<ProcedureMappingDialogProps>) {
  const [formData, setFormData] = useState<MapProcedureRequest>({
    procedureId: procedure.procedureId,
    serviceGroupId: procedure.serviceGroupId || 0,
    displayName: procedure.displayName || '',
    displayOrder: procedure.displayOrder,
  });

  const { data: groups } = useQuery({
    queryKey: ['serviceGroups'],
    queryFn: () => procedureApi.getServiceGroups(),
  });

  const mutation = useMutation({
    mutationFn: (data: MapProcedureRequest) => procedureApi.mapProcedure(data),
    onSuccess: () => onSaved(),
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (formData.serviceGroupId === 0) {
      alert('Please select a service group');
      return;
    }
    mutation.mutate(formData);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'number' ? Number(value) : value,
    }));
  };

  return (
    <div className="modal-overlay">
      <div className="modal">
        <h2>Map Procedure to Group</h2>
        <div className="procedure-info">
          <p><strong>Code:</strong> {procedure.procedureCode}</p>
          <p><strong>Name:</strong> {procedure.procedureName}</p>
          {procedure.procedureDescription && (
            <p><strong>Description:</strong> {procedure.procedureDescription}</p>
          )}
        </div>
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="serviceGroupId">Service Group *</label>
            <select
              id="serviceGroupId"
              name="serviceGroupId"
              value={formData.serviceGroupId}
              onChange={handleChange}
              required
            >
              <option value={0}>Select a group...</option>
              {groups?.map((group) => (
                <option key={group.id} value={group.id}>
                  {group.name}
                </option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label htmlFor="displayName">Display Name (optional)</label>
            <input
              type="text"
              id="displayName"
              name="displayName"
              value={formData.displayName || ''}
              onChange={handleChange}
              placeholder={procedure.procedureName}
            />
          </div>
          <div className="form-group">
            <label htmlFor="displayOrder">Display Order</label>
            <input
              type="number"
              id="displayOrder"
              name="displayOrder"
              value={formData.displayOrder}
              onChange={handleChange}
              min={0}
            />
          </div>
          <div className="modal-actions">
            <button type="button" className="secondary" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn-primary" disabled={mutation.isPending}>
              {mutation.isPending ? 'Saving...' : 'Save Mapping'}
            </button>
          </div>
          {mutation.isError && (
            <p className="error">Error saving mapping. Please try again.</p>
          )}
        </form>
      </div>
    </div>
  );
}
